// A job on /finance (`modal=line-job&lineJob=<id>`, finance-real, the user 2026-10-23), the one drawer that opens it on
// every tab: the Jobs tab's rows (and their status dropdown's Active for a job not started, `confirm=start`), and To do's
// rows to close and to fix, so To do stays behind it. It is the client page's job drawer, titled with its client, Agency
// for the own client. Its quote, and its client's page, open there; its invoice opens in its place (DD-045), on the
// page's invoice drawer.
import { useNavigate } from "react-router-dom";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { RetryBlock } from "../components/RetryBlock";
import { LineJobDrawer } from "../clientPage/LineJobDrawer";
import type { AgencyLineJob } from "../types";
import { clientName } from "../utils/names";
import { useAgencyLineJobDrawer } from "./hooks/useAgencyLineJobDrawer";

// The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const quoteWord = ENTITY_CONFIG.quote.label.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();

interface Props {
  jobId: string;
  starting: boolean;
  /** Its invoice, in this drawer's place. */
  onOpenInvoice: (invoiceId: string) => void;
  onClose: () => void;
}

export function AgencyLineJobDrawer({ jobId, starting, onOpenInvoice, onClose }: Props) {
  const { jobsQuery, job, clientQuery, quote, people } = useAgencyLineJobDrawer(jobId);
  const navigate = useNavigate();
  const trail = useTrailNavOptions(ENTITY_CONFIG.finance.label);
  // A job on a quote waits for it, as its status follows an open one. A read that failed is retried here; one that
  // answered without the job or its quote has nothing to retry.
  if (!jobsQuery.data || !job || (job.quoteId !== null && !quote)) {
    const readFailed = jobsQuery.isError || (job?.quoteId != null && clientQuery.isError);
    const missing = (jobsQuery.data && !job) || (job?.quoteId != null && clientQuery.data && !quote);
    const retry = () => {
      if (jobsQuery.isError) void jobsQuery.refetch();
      if (clientQuery.isError) void clientQuery.refetch();
    };
    return (
      <FinanceDrawer title={JOB.label} onClose={onClose}>
        {readFailed ? (
          <RetryBlock message={`Could not load this ${JOB.label.toLowerCase()}.`} retrying={jobsQuery.isFetching || clientQuery.isFetching} onRetry={retry} />
        ) : (
          <p className="text-sm text-gray-500">{missing ? `Could not load this ${JOB.label.toLowerCase()}.` : `Loading the ${JOB.label.toLowerCase()}…`}</p>
        )}
      </FinanceDrawer>
    );
  }
  /** Its client's page, where its quote lives, with that drawer open. */
  const onClientPage = (j: AgencyLineJob, query: Record<string, string>) => navigate(`/app/${j.client.id}/finance?${new URLSearchParams(query).toString()}`, trail);
  const actions: DropdownMenuEntry[] = [
    ...(job.quoteId ? [{ id: "quote", label: `Open its ${quoteWord}`, onClick: () => onClientPage(job, { view: "quotes", modal: "quote", quote: job.quoteId ?? "" }) }] : []),
    ...(job.invoice ? [{ id: "invoice", label: `Open its ${invoiceWord}`, onClick: () => onOpenInvoice(job.invoice?.id ?? "") }] : []),
    { id: "page", label: `Open ${job.client.name}'s page`, onClick: () => onClientPage(job, { modal: "line-job", lineJob: job.id }) },
  ];
  return (
    <LineJobDrawer
      key={job.id}
      job={job}
      quote={quote}
      people={people}
      own={job.ownClient}
      today={jobsQuery.data.today}
      actions={actions}
      starting={starting}
      titlePrefix={clientName(job.client.name, job.ownClient)}
      onOpenQuote={(quoteId) => onClientPage(job, { view: "quotes", modal: "quote", quote: quoteId })}
      onOpenInvoice={onOpenInvoice}
      onClose={onClose}
    />
  );
}
